"use client";

import { useState, useEffect, useCallback, useRef } from "react";

// Components
import Topbar from "@/components/Topbar";
import type { TabName } from "@/components/Topbar";
import Sidebar from "@/components/Sidebar";
import DossierView from "@/components/DossierView";
import OpportunitiesView from "@/components/OpportunitiesView";
import SlackAsksView from "@/components/SlackAsksView";
import SupportView from "@/components/SupportView";
import RetentionView from "@/components/RetentionView";
import Chatbot from "@/components/Chatbot";
import CommandPalette from "@/components/CommandPalette";
import Toast from "@/components/Toast";

// Data
import { MEMBERS } from "@/data/members";
import { PRIORITY_QUEUE } from "@/data/priority-queue";
import { OPPORTUNITIES } from "@/data/opportunities";
import { SLACK_ASKS } from "@/data/slack-asks";
import { SUPPORT_DATA } from "@/data/support";
import { RETENTION_DATA } from "@/data/retention";
import { CHAT_RESPONSES } from "@/data/chat-responses";

// ============================================
// Main Page — orchestrator
// ============================================

export default function Home() {
  const [currentMemberId, setCurrentMemberId] = useState("austin-dunbar");
  const [currentTab, setCurrentTab] = useState<TabName>("dossier");
  const [commandOpen, setCommandOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  // --- Actions ---

  const selectMember = useCallback((memberId: string) => {
    setCurrentMemberId(memberId);
    setCurrentTab("dossier");
    // Scroll main to top on next tick (after render)
    setTimeout(() => {
      mainRef.current?.scrollTo({ top: 0 });
    }, 0);
  }, []);

  const switchTab = useCallback((tab: TabName) => {
    setCurrentTab(tab);
    setTimeout(() => {
      mainRef.current?.scrollTo({ top: 0 });
    }, 0);
  }, []);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
  }, []);

  const clearToast = useCallback(() => {
    setToastMessage(null);
  }, []);

  const openCommand = useCallback(() => {
    setCommandOpen(true);
  }, []);

  const closeCommand = useCallback(() => {
    setCommandOpen(false);
  }, []);

  // --- Keyboard shortcuts ---

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;

      // "/" opens command palette (when not in input/textarea)
      if (
        e.key === "/" &&
        !e.ctrlKey &&
        !e.metaKey &&
        tag !== "INPUT" &&
        tag !== "TEXTAREA"
      ) {
        e.preventDefault();
        setCommandOpen(true);
      }

      // Escape closes command palette
      if (e.key === "Escape") {
        if (commandOpen) {
          setCommandOpen(false);
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [commandOpen]);

  // --- Current member for dossier ---

  const currentMember = MEMBERS[currentMemberId];

  return (
    <>
      <div className="app">
        <Topbar
          currentTab={currentTab}
          onTabChange={switchTab}
          onSearchOpen={openCommand}
        />
        <Sidebar
          members={MEMBERS}
          priorityQueue={PRIORITY_QUEUE}
          currentMemberId={currentMemberId}
          currentTab={currentTab}
          onSelectMember={selectMember}
        />
        <main className="main" ref={mainRef}>
          {currentTab === "dossier" && currentMember && (
            <DossierView
              member={currentMember}
              allMembers={MEMBERS}
              onSelectMember={selectMember}
              onShowToast={showToast}
            />
          )}
          {currentTab === "opportunities" && (
            <OpportunitiesView
              opportunities={OPPORTUNITIES}
              onShowToast={showToast}
            />
          )}
          {currentTab === "slack" && (
            <SlackAsksView
              slackAsks={SLACK_ASKS}
              onSelectMember={selectMember}
              onShowToast={showToast}
            />
          )}
          {currentTab === "support" && (
            <SupportView
              supportData={SUPPORT_DATA}
              onSelectMember={selectMember}
              onShowToast={showToast}
            />
          )}
          {currentTab === "retention" && (
            <RetentionView
              retentionData={RETENTION_DATA}
              onSelectMember={selectMember}
              onShowToast={showToast}
            />
          )}
        </main>
      </div>

      <Chatbot chatResponses={CHAT_RESPONSES} />
      <CommandPalette
        isOpen={commandOpen}
        onClose={closeCommand}
        members={MEMBERS}
        onSelectMember={selectMember}
      />
      <Toast message={toastMessage} onClear={clearToast} />
    </>
  );
}
